/**
 * CommissioningReview — Operator-facing review UX for model commissioning decisions.
 *
 * Phase 4.10 Track B β8 — WQ-243
 *
 * Surfaces /api/admin/ai/commissions/ to platform operators inside the BI Command
 * Center "Commissioning" tab. Filterable / searchable / paginated browser of model
 * commissions with click-to-detail expansion and approve/reject action modals.
 *
 * Pattern reuse:
 *   - Browser shape: WQ-244 LifecycleTimeline (filter strip + Load More + click-to-expand)
 *   - Action modal: WQ-237 AutoMLWizard / WQ-238 PredictionRequestForm (lighter — single
 *     decision with reason; no multi-step wizard)
 *
 * Backend integration (verified at session start, no scope growth):
 *   GET    /api/admin/ai/commissions/                — list with status + source filters
 *   GET    /api/admin/ai/commissions/{id}            — single commission detail
 *   POST   /api/admin/ai/commissions/{id}/approve    — body-less; reviewed_by from auth
 *   POST   /api/admin/ai/commissions/{id}/reject     — body { reason }
 *   GET    /api/admin/ai/decisions                   — best-effort gate-decision context
 *
 * approve_commission emits via lifecycle_hooks.on_commissioning_status_change
 * (WQ-225 lifecycle write) + PlatformNotificationService.emit_event. reject_commission
 * emits commission.rejected event. WQ-243 ships zero net new backend code.
 *
 * IL-3 thin pass-through (Owen 2026-04-27): operator UI surfaces governance-bearing
 * actions; audit weight derives from the action triggered, not net new logic.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { RefreshIcon, SearchIcon } from '../common/Icons';
import {
  Commission,
  listCommissions,
  approveCommission,
  rejectCommission,
} from '../../services/commissioningService';

// === Types ===

interface GateDecision {
  id: string;
  gate_name: string;
  decision: string;
  rationale_text: string | null;
  actor_type: string;
  decided_at: string | null;
  decision_metadata: Record<string, unknown>;
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

// Commission status enum (must match CommissionStatus on the backend).
const COMMISSION_STATUSES = [
  'discovered',
  'validating',
  'license_review',
  'staging',
  'approved',
  'rejected',
] as const;

const COMMISSION_SOURCES = ['huggingface', 'ollama', 'manual', 'api'] as const;

// "Pending" = anything not in a terminal state. Used for the count badge.
const PENDING_STATUSES = new Set([
  'discovered',
  'validating',
  'license_review',
  'staging',
]);

const STATUS_COLORS: Record<string, string> = {
  discovered: '#3b82f6',
  validating: '#eab308',
  license_review: '#a855f7',
  staging: '#06b6d4',
  approved: '#22c55e',
  rejected: '#ef4444',
};

const LICENSE_COLORS: Record<string, string> = {
  approved: '#22c55e',
  flagged: '#eab308',
  rejected: '#ef4444',
  unknown: '#6b7280',
};

const ORIGIN_COLORS: Record<string, string> = {
  approved: '#22c55e',
  blocked: '#ef4444',
  unverified: '#6b7280',
};

const DECISION_BADGE_COLORS: Record<string, string> = {
  approved: '#22c55e',
  rejected: '#ef4444',
  deferred: '#eab308',
  override: '#a855f7',
};

const PAGE_SIZE = 50;
const REJECT_REASON_MIN = 10;
const REJECT_REASON_MAX = 2000;

// === Inline gate-decision fetch (best-effort context only) ===

async function listDecisions(
  authFetch: FetchFn,
  filters: { gate_name?: string; limit?: number },
): Promise<GateDecision[]> {
  const params = new URLSearchParams();
  if (filters.gate_name) params.set('gate_name', filters.gate_name);
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  const qs = params.toString();
  const res = await authFetch(`/api/admin/ai/decisions${qs ? '?' + qs : ''}`);
  if (!res.ok) throw new Error(`Failed to load decisions: ${res.status}`);
  return res.json();
}

// === Helpers ===

function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function ageInHours(iso: string): number {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    return Math.max(0, Math.floor(ms / (1000 * 60 * 60)));
  } catch {
    return 0;
  }
}

function formatAge(iso: string | null | undefined): string {
  if (!iso) return '—';
  const hours = ageInHours(iso);
  if (hours < 1) return '<1h';
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

// === Component ===

const CommissioningReview: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();

  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [decisionsForSelected, setDecisionsForSelected] = useState<GateDecision[] | null>(null);
  const [decisionsLoading, setDecisionsLoading] = useState(false);

  const [actionModal, setActionModal] = useState<
    { type: 'approve' | 'reject'; commission: Commission } | null
  >(null);
  const [actionReason, setActionReason] = useState('');
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: { status?: string; source?: string } = {};
      if (statusFilter) filters.status = statusFilter;
      if (sourceFilter) filters.source = sourceFilter;
      const rows = await listCommissions(authFetch, filters);
      setCommissions(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load commissions');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, statusFilter, sourceFilter]);

  // 30s polling — refresh while no action modal is open. Polling pauses while
  // the operator is mid-decision so the table doesn't jump under them.
  useEffect(() => {
    if (authLoading || actionModal) return;
    const t = setInterval(() => {
      fetchData();
    }, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, actionModal, statusFilter, sourceFilter]);

  const filtered = useMemo(() => {
    if (!searchQuery) return commissions;
    const q = searchQuery.toLowerCase();
    return commissions.filter(
      c =>
        c.source_model_id.toLowerCase().includes(q) ||
        c.source.toLowerCase().includes(q) ||
        (c.license_id || '').toLowerCase().includes(q) ||
        (c.origin_country || '').toLowerCase().includes(q) ||
        c.id.toLowerCase().includes(q),
    );
  }, [commissions, searchQuery]);

  const paged = useMemo(() => filtered.slice(0, PAGE_SIZE), [filtered]);

  const pendingCount = useMemo(
    () => commissions.filter(c => PENDING_STATUSES.has(c.commission_status)).length,
    [commissions],
  );

  const selectedCommission = useMemo(
    () => (selectedId ? commissions.find(c => c.id === selectedId) || null : null),
    [selectedId, commissions],
  );

  const handleRowClick = async (commission: Commission) => {
    if (selectedId === commission.id) {
      setSelectedId(null);
      setDecisionsForSelected(null);
      return;
    }
    setSelectedId(commission.id);
    setDecisionsForSelected(null);
    setDecisionsLoading(true);
    try {
      // Best-effort: fetch decisions tagged with a commission gate name, then
      // narrow client-side to those whose decision_metadata references this
      // commission. The decision recorder doesn't yet stamp commission_id,
      // so this surfaces gate-level context (license / origin / commission_gate)
      // without claiming row-level fidelity.
      const decisions = await listDecisions(authFetch, { limit: 100 });
      const safe = Array.isArray(decisions) ? decisions : [];
      const relevant = safe.filter(d => {
        const meta = d.decision_metadata || {};
        if (meta.commission_id === commission.id) return true;
        if (meta.source_model_id === commission.source_model_id) return true;
        const gate = (d.gate_name || '').toLowerCase();
        return gate.includes('commission') || gate.includes('license') || gate.includes('origin');
      });
      setDecisionsForSelected(relevant.slice(0, 25));
    } catch {
      setDecisionsForSelected([]);
    }
    setDecisionsLoading(false);
  };

  const openAction = (type: 'approve' | 'reject', commission: Commission) => {
    setActionModal({ type, commission });
    setActionReason('');
    setActionError(null);
  };

  const closeAction = () => {
    setActionModal(null);
    setActionReason('');
    setActionError(null);
    setActionSubmitting(false);
  };

  const submitAction = async () => {
    if (!actionModal) return;
    const { type, commission } = actionModal;
    if (type === 'reject') {
      const trimmed = actionReason.trim();
      if (trimmed.length < REJECT_REASON_MIN) {
        setActionError(`Reason must be at least ${REJECT_REASON_MIN} characters.`);
        return;
      }
      if (trimmed.length > REJECT_REASON_MAX) {
        setActionError(`Reason must be at most ${REJECT_REASON_MAX} characters.`);
        return;
      }
    }
    setActionSubmitting(true);
    setActionError(null);
    try {
      const updated =
        type === 'approve'
          ? await approveCommission(authFetch, commission.id)
          : await rejectCommission(authFetch, commission.id, actionReason.trim());
      // Optimistic merge — replace the row in-place; full refresh follows.
      setCommissions(prev => prev.map(c => (c.id === updated.id ? updated : c)));
      closeAction();
      // Background reconciliation
      fetchData();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : `Failed to ${type} commission`);
      setActionSubmitting(false);
    }
  };

  const isActionable = (commission: Commission): boolean =>
    !['approved', 'rejected'].includes(commission.commission_status);

  if (loading && commissions.length === 0) {
    return (
      <div
        data-testid="commissioning-loading"
        style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}
      >
        Loading commissions...
      </div>
    );
  }

  return (
    <div data-testid="commissioning-review">
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: 16, color: 'var(--text-primary)' }}>
            Commissioning Decisions
          </h3>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
            <span data-testid="commissioning-pending-count">
              {pendingCount} pending
            </span>
            {' · '}
            {commissions.length} total
          </div>
        </div>
      </div>

      {/* Filter row */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          marginBottom: 16,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
          <SearchIcon
            size={16}
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-secondary)',
            }}
          />
          <input
            type="text"
            placeholder="Search source / model / license..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            data-testid="commissioning-search"
            style={{
              width: '100%',
              padding: '8px 8px 8px 32px',
              borderRadius: 6,
              border: '1px solid var(--border)',
              background: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              fontSize: 13,
            }}
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          data-testid="commissioning-status-filter"
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            border: '1px solid var(--border)',
            background: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
            fontSize: 13,
          }}
        >
          <option value="">All Statuses</option>
          {COMMISSION_STATUSES.map(s => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={sourceFilter}
          onChange={e => setSourceFilter(e.target.value)}
          data-testid="commissioning-source-filter"
          style={{
            padding: '8px 12px',
            borderRadius: 6,
            border: '1px solid var(--border)',
            background: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
            fontSize: 13,
          }}
        >
          <option value="">All Sources</option>
          {COMMISSION_SOURCES.map(s => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button
          onClick={() => fetchData()}
          data-testid="commissioning-refresh"
          style={{
            fontSize: 13,
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid var(--border)',
            fontWeight: 500,
            cursor: 'pointer',
            background: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <RefreshIcon size={14} /> Refresh
        </button>
      </div>

      {error && (
        <div
          data-testid="commissioning-error"
          style={{
            padding: 12,
            background: '#fef2f2',
            color: '#dc2626',
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}

      {/* Table */}
      {paged.length === 0 ? (
        <div
          data-testid="commissioning-empty"
          style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}
        >
          {searchQuery || statusFilter || sourceFilter
            ? 'No commissions match the current filters.'
            : 'No commissions yet. Run discovery or start a manual commission to populate this view.'}
        </div>
      ) : (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 8,
            overflow: 'hidden',
            background: 'var(--bg-secondary)',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-primary)', color: 'var(--text-secondary)' }}>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>
                  Source
                </th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>
                  Model
                </th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>
                  License
                </th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>
                  Origin
                </th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>
                  Status
                </th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>
                  Submitted
                </th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>
                  Age
                </th>
                <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 600 }}>
                  Reviewer
                </th>
              </tr>
            </thead>
            <tbody>
              {paged.map(c => {
                const isOpen = selectedId === c.id;
                return (
                  <React.Fragment key={c.id}>
                    <tr
                      onClick={() => handleRowClick(c)}
                      data-testid={`commissioning-row-${c.id}`}
                      style={{
                        cursor: 'pointer',
                        borderTop: '1px solid var(--border)',
                        background: isOpen ? 'var(--bg-primary)' : undefined,
                        color: 'var(--text-primary)',
                      }}
                    >
                      <td style={{ padding: '10px 12px' }}>{c.source}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 500 }}>
                        {c.source_model_id}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span
                          style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            borderRadius: 8,
                            background: `${LICENSE_COLORS[c.license_status] || '#6b7280'}20`,
                            color: LICENSE_COLORS[c.license_status] || '#6b7280',
                            fontWeight: 600,
                          }}
                        >
                          {c.license_status}
                        </span>
                        {c.license_id && (
                          <span
                            style={{
                              marginLeft: 6,
                              fontSize: 11,
                              color: 'var(--text-secondary)',
                            }}
                          >
                            {c.license_id}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span
                          style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            borderRadius: 8,
                            background: `${ORIGIN_COLORS[c.origin_status] || '#6b7280'}20`,
                            color: ORIGIN_COLORS[c.origin_status] || '#6b7280',
                            fontWeight: 600,
                          }}
                        >
                          {c.origin_status}
                        </span>
                        {c.origin_country && (
                          <span
                            style={{
                              marginLeft: 6,
                              fontSize: 11,
                              color: 'var(--text-secondary)',
                            }}
                          >
                            {c.origin_country}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span
                          data-testid={`commissioning-status-${c.id}`}
                          style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            borderRadius: 8,
                            background: `${STATUS_COLORS[c.commission_status] || '#6b7280'}20`,
                            color: STATUS_COLORS[c.commission_status] || '#6b7280',
                            fontWeight: 600,
                          }}
                        >
                          {c.commission_status}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>
                        {formatRelativeTime(c.created_at)}
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>
                        {formatAge(c.created_at)}
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>
                        {c.reviewed_by || '—'}
                      </td>
                    </tr>
                    {isOpen && selectedCommission && (
                      <tr
                        data-testid={`commissioning-detail-${c.id}`}
                        style={{ background: 'var(--bg-primary)' }}
                      >
                        <td colSpan={8} style={{ padding: 16 }}>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                              gap: 16,
                              marginBottom: 16,
                            }}
                          >
                            <div>
                              <div
                                style={{
                                  fontSize: 11,
                                  textTransform: 'uppercase',
                                  color: 'var(--text-secondary)',
                                  marginBottom: 4,
                                }}
                              >
                                Commission ID
                              </div>
                              <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>
                                {selectedCommission.id}
                              </div>
                            </div>
                            <div>
                              <div
                                style={{
                                  fontSize: 11,
                                  textTransform: 'uppercase',
                                  color: 'var(--text-secondary)',
                                  marginBottom: 4,
                                }}
                              >
                                Commissioned By
                              </div>
                              <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>
                                {selectedCommission.commissioned_by}
                              </div>
                            </div>
                            <div>
                              <div
                                style={{
                                  fontSize: 11,
                                  textTransform: 'uppercase',
                                  color: 'var(--text-secondary)',
                                  marginBottom: 4,
                                }}
                              >
                                Last Updated
                              </div>
                              <div style={{ fontSize: 12, color: 'var(--text-primary)' }}>
                                {formatRelativeTime(selectedCommission.updated_at)}
                              </div>
                            </div>
                            {selectedCommission.rejection_reason && (
                              <div style={{ gridColumn: '1 / -1' }}>
                                <div
                                  style={{
                                    fontSize: 11,
                                    textTransform: 'uppercase',
                                    color: 'var(--text-secondary)',
                                    marginBottom: 4,
                                  }}
                                >
                                  Rejection Reason
                                </div>
                                <div
                                  data-testid={`commissioning-rejection-reason-${c.id}`}
                                  style={{ fontSize: 12, color: 'var(--text-primary)' }}
                                >
                                  {selectedCommission.rejection_reason}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Decision context */}
                          <div
                            data-testid={`commissioning-decisions-${c.id}`}
                            style={{
                              padding: 12,
                              background: 'var(--bg-secondary)',
                              borderRadius: 6,
                              border: '1px dashed var(--border)',
                              marginBottom: 16,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                marginBottom: 8,
                                color: 'var(--text-primary)',
                              }}
                            >
                              Gate Decision Context
                            </div>
                            {decisionsLoading ? (
                              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                Loading decisions…
                              </div>
                            ) : !decisionsForSelected || decisionsForSelected.length === 0 ? (
                              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                No related gate decisions surfaced.
                              </div>
                            ) : (
                              <ul
                                style={{
                                  margin: 0,
                                  paddingLeft: 16,
                                  fontSize: 12,
                                  color: 'var(--text-primary)',
                                }}
                              >
                                {decisionsForSelected.map(d => (
                                  <li key={d.id} style={{ marginBottom: 6 }}>
                                    <span
                                      style={{
                                        fontSize: 10,
                                        padding: '1px 6px',
                                        borderRadius: 8,
                                        marginRight: 6,
                                        background: `${
                                          DECISION_BADGE_COLORS[d.decision] || '#6b7280'
                                        }20`,
                                        color: DECISION_BADGE_COLORS[d.decision] || '#6b7280',
                                        fontWeight: 600,
                                      }}
                                    >
                                      {d.decision}
                                    </span>
                                    <strong>{d.gate_name}</strong>
                                    {' · '}
                                    <span style={{ color: 'var(--text-secondary)' }}>
                                      {d.actor_type}
                                    </span>
                                    {d.rationale_text && (
                                      <div style={{ marginTop: 2, color: 'var(--text-secondary)' }}>
                                        {d.rationale_text}
                                      </div>
                                    )}
                                    <div
                                      style={{
                                        marginTop: 2,
                                        color: 'var(--text-secondary)',
                                        fontSize: 10,
                                      }}
                                    >
                                      {formatRelativeTime(d.decided_at)}
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>

                          {/* Action panel */}
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              type="button"
                              onClick={e => {
                                e.stopPropagation();
                                openAction('approve', selectedCommission);
                              }}
                              disabled={!isActionable(selectedCommission)}
                              data-testid={`commissioning-approve-${c.id}`}
                              style={{
                                fontSize: 13,
                                padding: '8px 16px',
                                borderRadius: 6,
                                border: 'none',
                                fontWeight: 600,
                                cursor: isActionable(selectedCommission)
                                  ? 'pointer'
                                  : 'not-allowed',
                                background: isActionable(selectedCommission)
                                  ? '#22c55e'
                                  : 'var(--bg-secondary)',
                                color: isActionable(selectedCommission)
                                  ? '#ffffff'
                                  : 'var(--text-secondary)',
                                opacity: isActionable(selectedCommission) ? 1 : 0.6,
                              }}
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              onClick={e => {
                                e.stopPropagation();
                                openAction('reject', selectedCommission);
                              }}
                              disabled={!isActionable(selectedCommission)}
                              data-testid={`commissioning-reject-${c.id}`}
                              style={{
                                fontSize: 13,
                                padding: '8px 16px',
                                borderRadius: 6,
                                border: '1px solid #ef4444',
                                fontWeight: 600,
                                cursor: isActionable(selectedCommission)
                                  ? 'pointer'
                                  : 'not-allowed',
                                background: 'transparent',
                                color: '#ef4444',
                                opacity: isActionable(selectedCommission) ? 1 : 0.6,
                              }}
                            >
                              Reject
                            </button>
                            {!isActionable(selectedCommission) && (
                              <span
                                style={{
                                  fontSize: 12,
                                  color: 'var(--text-secondary)',
                                  alignSelf: 'center',
                                }}
                              >
                                Terminal status — no actions available.
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {filtered.length > PAGE_SIZE && (
        <div
          style={{
            fontSize: 12,
            color: 'var(--text-secondary)',
            marginTop: 12,
            textAlign: 'center',
          }}
        >
          Showing first {PAGE_SIZE} of {filtered.length} matching commissions. Narrow the
          filters to see more.
        </div>
      )}

      {/* Action modal */}
      {actionModal && (
        <div
          data-testid="commissioning-action-modal"
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: 'var(--bg-primary)',
              border: '1px solid var(--border)',
              borderRadius: 12,
              padding: 24,
              width: '100%',
              maxWidth: 520,
              boxShadow: '0 20px 50px rgba(0,0,0,0.4)',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: 16,
                color: 'var(--text-primary)',
                marginBottom: 12,
              }}
            >
              {actionModal.type === 'approve' ? 'Approve' : 'Reject'} commission?
            </h3>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
              <strong>{actionModal.commission.source}</strong>:{' '}
              {actionModal.commission.source_model_id}
              <br />
              {actionModal.type === 'approve'
                ? 'A platform model will be created in the registry. This emits a lifecycle event and a commission.approved notification.'
                : 'The commission will be marked rejected and a commission.rejected notification will be emitted. A reason is required for the audit trail.'}
            </div>

            {actionModal.type === 'reject' && (
              <div style={{ marginBottom: 16 }}>
                <label
                  htmlFor="commissioning-reject-reason"
                  style={{
                    display: 'block',
                    fontSize: 12,
                    color: 'var(--text-secondary)',
                    marginBottom: 4,
                  }}
                >
                  Rejection reason (min {REJECT_REASON_MIN}, max {REJECT_REASON_MAX} chars)
                </label>
                <textarea
                  id="commissioning-reject-reason"
                  data-testid="commissioning-reject-reason-input"
                  value={actionReason}
                  onChange={e => setActionReason(e.target.value)}
                  rows={4}
                  maxLength={REJECT_REASON_MAX}
                  style={{
                    width: '100%',
                    padding: 8,
                    borderRadius: 6,
                    border: '1px solid var(--border)',
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    fontSize: 13,
                    fontFamily: 'inherit',
                    resize: 'vertical',
                  }}
                />
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--text-secondary)',
                    textAlign: 'right',
                    marginTop: 2,
                  }}
                >
                  {actionReason.trim().length} / {REJECT_REASON_MAX}
                </div>
              </div>
            )}

            {actionError && (
              <div
                data-testid="commissioning-action-error"
                style={{
                  padding: 10,
                  background: '#fef2f2',
                  color: '#dc2626',
                  borderRadius: 6,
                  marginBottom: 12,
                  fontSize: 12,
                }}
              >
                {actionError}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={closeAction}
                disabled={actionSubmitting}
                data-testid="commissioning-action-cancel"
                style={{
                  fontSize: 13,
                  padding: '8px 16px',
                  borderRadius: 6,
                  border: '1px solid var(--border)',
                  cursor: actionSubmitting ? 'not-allowed' : 'pointer',
                  background: 'transparent',
                  color: 'var(--text-primary)',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitAction}
                disabled={
                  actionSubmitting ||
                  (actionModal.type === 'reject' &&
                    actionReason.trim().length < REJECT_REASON_MIN)
                }
                data-testid="commissioning-action-confirm"
                style={{
                  fontSize: 13,
                  padding: '8px 16px',
                  borderRadius: 6,
                  border: 'none',
                  fontWeight: 600,
                  cursor:
                    actionSubmitting ||
                    (actionModal.type === 'reject' &&
                      actionReason.trim().length < REJECT_REASON_MIN)
                      ? 'not-allowed'
                      : 'pointer',
                  background: actionModal.type === 'approve' ? '#22c55e' : '#ef4444',
                  color: '#ffffff',
                  opacity:
                    actionSubmitting ||
                    (actionModal.type === 'reject' &&
                      actionReason.trim().length < REJECT_REASON_MIN)
                      ? 0.6
                      : 1,
                }}
              >
                {actionSubmitting
                  ? 'Submitting…'
                  : actionModal.type === 'approve'
                    ? 'Approve'
                    : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissioningReview;
